import * as React from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import { useUI } from '../contexts/UIProvider';
import type { Band } from '../types';
import UsersIcon from './icons/UsersIcon';
import PlusCircleIcon from './icons/PlusCircleIcon';
import MapPinIcon from './icons/MapPinIcon';
import * as api from '../services/apiService';
import CloseIcon from './icons/CloseIcon';
import CheckIcon from './icons/CheckIcon';
import { loadGoogleMaps } from '../utils/googleMapsLoader';

const MyProjectsPage: React.FC = () => {
    const { currentUser } = useAuth();
    const { bands, handleViewBand, refreshBands } = useNavigation();
    const { showToast } = useUI();
    const [isCreating, setIsCreating] = React.useState(false);

    const myProjects = React.useMemo(() => {
        if (!currentUser) return [];
        return bands.filter(band => 
            band.members.some(m => m.handle === currentUser.handle)
        );
    }, [currentUser, bands]);

    const handleCreateProject = async (name: string, city: string, region: string, country: string) => {
        try {
            await api.createBand(name, city, region, country);
            await refreshBands();
            showToast("New project created successfully!");
            setIsCreating(false);
        } catch (error) {
            showToast(error instanceof Error ? error.message : "Failed to create project.");
        }
    };

    return (
        <div className="space-y-6 font-urbanist">
            <div className="bg-brand-surface rounded-lg p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 border border-gray-800 shadow-sm">
                <div className="flex items-center space-x-3">
                    <div className="bg-brand-orange/10 p-3 rounded-xl">
                        <UsersIcon className="h-7 w-7 text-brand-orange" />
                    </div>
                    <div>
                        <h2 className="text-3xl font-extrabold text-white tracking-tight">My Projects</h2>
                        <p className="text-sm text-brand-muted">Manage your bands, ensembles, and creative productions.</p>
                    </div>
                </div>
                <button
                    onClick={() => setIsCreating(true)}
                    className="flex items-center justify-center space-x-2 bg-brand-orange text-white font-bold px-6 py-3 rounded-full hover:bg-orange-600 transition-all text-sm tracking-widest shadow-lg shadow-brand-orange/20"
                >
                    <PlusCircleIcon className="h-5 w-5" />
                    <span>Create New Project</span>
                </button>
            </div>

            {myProjects.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {myProjects.map(project => (
                        <ProjectCard 
                            key={project.id} 
                            project={project} 
                            onView={() => handleViewBand(project)}
                        />
                    ))}
                </div>
            ) : (
                <div className="bg-brand-surface rounded-lg p-12 text-center border border-dashed border-gray-700 shadow-inner">
                    <UsersIcon className="h-16 w-16 text-gray-700 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2 tracking-tight">No projects yet</h3>
                    <p className="text-brand-muted max-w-sm mx-auto">
                        Once you add projects to your profile or join existing ones, they will appear here for easy management.
                    </p>
                </div>
            )}

            {isCreating && <CreateProjectModal onClose={() => setIsCreating(false)} onSubmit={handleCreateProject} />}
        </div>
    );
};

const CreateProjectModal: React.FC<{ onClose: () => void, onSubmit: (name: string, city: string, region: string, country: string) => void }> = ({ onClose, onSubmit }) => {
    const [name, setName] = React.useState('');
    const [city, setCity] = React.useState('');
    const [region, setRegion] = React.useState('');
    const [country, setCountry] = React.useState('');
    const cityInputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        const init = async () => {
            const API_KEY = (window as any).GOOGLE_MAPS_API_KEY || "PASTE_YOUR_GOOGLE_MAPS_API_KEY_HERE";
            await loadGoogleMaps(API_KEY);
            if (!(window as any).google || !cityInputRef.current) return;
            const autocomplete = new (window as any).google.maps.places.Autocomplete(cityInputRef.current, { 
                fields: ['address_components', 'formatted_address', 'name']
            });
            autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();
                if (place.address_components) {
                    let c = '';
                    let r = '';
                    let cnt = '';
                    for (const component of place.address_components) {
                        const types = component.types;
                        if (types.includes('locality') || types.includes('sublocality')) c = component.long_name;
                        if (types.includes('administrative_area_level_1')) r = component.long_name;
                        if (types.includes('country')) cnt = component.long_name;
                    }
                    setCity(c || place.name || '');
                    setRegion(r);
                    setCountry(cnt);
                }
            });
        };
        init();
    }, []);

    const locationDisplay = city 
        ? `${city}${region ? `, ${region}` : ''}${country ? `, ${country}` : ''}`
        : '';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <div className="bg-brand-surface w-full max-w-md rounded-2xl p-6 border border-gray-700 shadow-2xl relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-brand-muted hover:text-white"><CloseIcon className="h-6 w-6"/></button>
                <h3 className="text-2xl font-bold text-white mb-4 tracking-tight">Create New Project</h3>
                <div className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-brand-muted mb-1 tracking-widest">Project / Band Name</label>
                        <input value={name} onChange={e => setName(e.target.value)} className="w-full bg-gray-700 text-white rounded-lg p-3 outline-none focus:ring-1 focus:ring-brand-orange border border-gray-600 font-bold" placeholder="e.g., Midnight Grooves" />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-brand-muted mb-1 tracking-widest">Base City</label>
                        <div className="relative">
                            <input ref={cityInputRef} value={locationDisplay} onChange={e => setCity(e.target.value)} className="w-full bg-gray-700 text-white rounded-lg p-3 outline-none focus:ring-1 focus:ring-brand-orange border border-gray-600 font-bold pr-10" placeholder="Search city..." />
                            {city && <CheckIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-green-400" />}
                        </div>
                        {city && (
                            <p className="text-[10px] text-brand-muted mt-1 uppercase font-bold tracking-widest">Indexed for search: <span className="text-brand-orange">{city}</span></p>
                        )}
                    </div>
                    <button 
                        onClick={() => onSubmit(name, city, region, country)}
                        disabled={!name || !city}
                        className="w-full bg-brand-orange text-white font-bold py-3 rounded-full mt-4 hover:bg-orange-600 transition-colors tracking-widest text-sm shadow-lg shadow-brand-orange/20 disabled:opacity-50"
                    >
                        Post
                    </button>
                </div>
            </div>
        </div>
    );
};

interface ProjectCardProps {
    project: Band;
    onView: () => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, onView }) => {
    return (
        <div className="bg-brand-surface rounded-xl p-5 border border-gray-800 flex flex-col hover:border-brand-orange/30 transition-all shadow-lg group">
            <div className="flex items-start space-x-4">
                <img 
                    src={project.avatarUrl} 
                    alt={project.name} 
                    className="h-20 w-20 rounded-xl object-cover border-2 border-gray-800 group-hover:border-brand-orange/50 transition-colors shadow-md" 
                />
                <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-bold text-white tracking-tight truncate">{project.name}</h3>
                    <div className="flex items-center text-brand-muted text-sm mt-1">
                        <MapPinIcon className="h-3.5 w-3.5 mr-1 text-brand-orange" />
                        <span className="truncate">{project.city}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                        <span className="bg-gray-800 text-gray-300 text-[10px] font-bold px-2 py-0.5 rounded tracking-widest">
                            {project.members.length} Member{project.members.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                </div>
            </div>
            
            <div className="mt-6 pt-4 border-t border-gray-800/50 flex gap-3">
                <button
                    onClick={onView}
                    className="flex-1 bg-brand-orange/10 hover:bg-brand-orange/20 text-brand-orange font-bold py-2 rounded-full text-xs tracking-widest transition-colors flex items-center justify-center gap-2"
                >
                    View Page & Manage
                </button>
            </div>
        </div>
    );
};

export default MyProjectsPage;